import { useMemo, useState } from 'react';

import { orderBy } from 'lodash';

import {
  ColoradoDragGuideEntertainerQuotes,
  CreateColoradoDragGuideQuotePayload,
  EntertainerQuotes,
  ScheduleEntertainerQuotePayload,
  UpdateColoradoDragGuideQuotePayload,
  useColoradoDragGuideQuotesQuery,
  useCreateColoradoDragGuideEntertainerQuoteMutation,
  useScheduleColoradoDragGuideEntertainerQuoteMutation,
  useUpdateColoradoDragGuideEntertainerQuoteMutation,
} from '@admin/queries/AdminEntertainerQuotesQueries';

import { getApiUrl } from '@api/API';

import { Select } from '@components/form/Select';
import { Screen } from '@components/layout/Screen';

import { useEventQuery } from '../../queries/EventQueries';
import { EntertainerQuotesSection } from './components/EntertainerQuotesSection';

enum Filter {
  UpcomingEvent = 'upcomingEvent',
  ColoradoDragRoyale = 'coloradoDragRoyale',
}

const FILTER_RECORD: Record<
  Filter,
  { label: string; filterFn: (entertainerQuotes: ColoradoDragGuideEntertainerQuotes) => void }
> = {
  [Filter.UpcomingEvent]: {
    label: 'Upcoming Event',
    filterFn: (entertainerQuotes) => {},
  },
  [Filter.ColoradoDragRoyale]: {
    label: 'Colorado Drag Royale',
    filterFn: (entertainerQuotes) => {},
  },
};

enum OrderBy {
  Followers = 'followers',
  CreatedAt = 'createdAt',
}

const ORDER_BY_RECORD: Record<
  OrderBy,
  {
    label: string;
    orderFn: <QuoteSource extends unknown>(
      entertainerQuotes: EntertainerQuotes<QuoteSource>,
    ) => void;
  }
> = {
  [OrderBy.Followers]: {
    label: 'Followers',
    orderFn: ({ entertainerFollowerCount }) => entertainerFollowerCount,
  },
  [OrderBy.CreatedAt]: {
    label: 'Latest',
    orderFn: ({ quotesAboutEntertainer }) => quotesAboutEntertainer?.[0]?.createdAt,
  },
};

export const useSortEntertainerQuotes = <QuoteSource extends unknown>(
  entertainerQuotes: EntertainerQuotes<QuoteSource>[],
) => {
  const [orderEntertainerBy, setOrderEntertainerBy] = useState<OrderBy>(OrderBy.CreatedAt);

  const sortedEntertainerQuotes = useMemo(
    () =>
      orderBy(
        entertainerQuotes,
        [
          ORDER_BY_RECORD[orderEntertainerBy].orderFn,
          ({ entertainerFollowerCount }) => entertainerFollowerCount,
          ({ quotesAboutEntertainer }) => quotesAboutEntertainer.length,
          ({ entertainer }) => entertainer.title,
        ],
        ['desc', 'desc', 'desc', 'asc'],
      ),
    [entertainerQuotes, orderEntertainerBy],
  );

  return {
    sortedEntertainerQuotes,
    entertainerQuotesOrderOptions: Object.entries(ORDER_BY_RECORD).map(([value, { label }]) => ({
      label,
      value,
    })),
    orderEntertainerBy,
    updateOrderEntertainerBy: setOrderEntertainerBy,
  };
};

export const SuperAdminManageCDGQuotesScreen = () => {
  const {
    coloradoDragGuideQuotesAreReady,
    coloradoDragGuideQuotes = [],
    refetchColoradoDragGuideQuotes,
  } = useColoradoDragGuideQuotesQuery();

  const [selectedFilter, setSelectedFilter] = useState<Filter | 'all'>('all');
  const { event } = useEventQuery('6a4ea04005d24cdced71c3ee');

  const {
    sortedEntertainerQuotes,
    entertainerQuotesOrderOptions,
    orderEntertainerBy,
    updateOrderEntertainerBy,
  } = useSortEntertainerQuotes(coloradoDragGuideQuotes);

  const { createQuoteAsync } = useCreateColoradoDragGuideEntertainerQuoteMutation();
  const { updateQuoteAsync } = useUpdateColoradoDragGuideEntertainerQuoteMutation();
  const { scheduleQuoteAsync } = useScheduleColoradoDragGuideEntertainerQuoteMutation();

  const filteredQuotes = useMemo(() => {
    if (selectedFilter === Filter.ColoradoDragRoyale) {
      const cdgRoyaleEntertainerIds = event.entertainers.map((entertainer: any) => entertainer._id);
      return sortedEntertainerQuotes.filter(({ entertainer }) =>
        cdgRoyaleEntertainerIds.includes(entertainer._id),
      );
    }

    return sortedEntertainerQuotes;
  }, [sortedEntertainerQuotes, selectedFilter]);

  const handleCreateQuote = async (payload: CreateColoradoDragGuideQuotePayload) => {
    const response = await createQuoteAsync(payload);
    refetchColoradoDragGuideQuotes();

    return response;
  };

  const handleUpdateQuote = async (payload: UpdateColoradoDragGuideQuotePayload) => {
    await updateQuoteAsync(payload);
    refetchColoradoDragGuideQuotes();
  };

  const handleScheduleQuote = async (payload: ScheduleEntertainerQuotePayload) => {
    await scheduleQuoteAsync(payload);
    refetchColoradoDragGuideQuotes();
  };

  const getPreviewUrl = ({ quoteId, quoteFontSize }: { quoteId: string; quoteFontSize: number }) =>
    `${getApiUrl()}/v1/colorado-drag-guide-quotes/${quoteId}/quote.jpg?quoteFontSize=${quoteFontSize}`;

  return (
    <Screen
      name="Super Admin Manage CDG Quotes Screen"
      headerProps={{ title: 'Colorado Drag Guide Quotes' }}
      hideToolbar
      disableLocationPermissionGate
    >
      <Screen.Content maxWidth={425}>
        <div className="mb-4 d-grid gap-2">
          <Select
            options={[
              { label: 'All', value: 'all' },
              ...Object.entries(FILTER_RECORD).map(([value, { label }]) => ({ label, value })),
            ]}
            value={selectedFilter}
            onChange={(e) => setSelectedFilter(e.target.value as Filter)}
          />
          <Select
            options={entertainerQuotesOrderOptions}
            value={orderEntertainerBy}
            onChange={(e) => updateOrderEntertainerBy(e.target.value as OrderBy)}
          />
        </div>

        {!coloradoDragGuideQuotesAreReady ? (
          <div>Loading...</div>
        ) : !coloradoDragGuideQuotes?.length ? (
          <div>No quotes found.</div>
        ) : (
          filteredQuotes.map((entertainerQuotes) => (
            <EntertainerQuotesSection
              key={entertainerQuotes.entertainer._id}
              entertainerQuotes={entertainerQuotes}
              previewUrl={getPreviewUrl}
              onCreate={handleCreateQuote}
              onUpdate={handleUpdateQuote}
              onSchedule={handleScheduleQuote}
            />
          ))
        )}
      </Screen.Content>
    </Screen>
  );
};
